import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, logout } from "./api";
import { formatDayLong, fromISO, toISO, todayISO } from "./dates";
import type { Client, Entry } from "./db";
import { DEFAULT_ACCENT, groupByClient } from "./pdf";

// GitHub katkı takvimi yeşilleri: boş gün nötr gri, sonra tek tonlu açık → koyu
const HEAT = ["#ebedf0", "#9be9a8", "#40c463", "#30a14e", "#216e39"];
const BAR = "#30a14e";

// Sabit eşikler: yıllar arası karşılaştırılabilir kalsın (1 · 2 · 3–4 · 5+ kayıt)
const level = (n: number) => (n === 0 ? 0 : n === 1 ? 1 : n === 2 ? 2 : n <= 4 ? 3 : 4);
const LEVEL_TIPS = ["Kayıt yok", "1 kayıt", "2 kayıt", "3–4 kayıt", "5+ kayıt"];

const WEEKDAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const fmtMonth = new Intl.DateTimeFormat("tr-TR", { month: "short" });
const fmtShort = new Intl.DateTimeFormat("tr-TR", { day: "numeric", month: "short" });

const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const mondayOf = (d: Date) => addDays(d, -((d.getDay() + 6) % 7));
const weekdayIndex = (iso: string) => (fromISO(iso).getDay() + 6) % 7;

type Tip = { x: number; y: number; text: ReactNode } | null;

export function Analytics() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const year = Number(searchParams.get("yil")) || 0; // 0 = son 12 ay
  const today = todayISO();

  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [minYear, setMinYear] = useState(fromISO(today).getFullYear());
  const [error, setError] = useState<string | null>(null);
  const [tip, setTip] = useState<Tip>(null);

  // Görünen aralık: takvim pazartesiden başlar
  const { from, to, label } = useMemo(() => {
    if (year) return { from: `${year}-01-01`, to: `${year}-12-31`, label: `${year} yılında` };
    const start = mondayOf(addDays(fromISO(today), -364));
    return { from: toISO(start), to: today, label: "son 12 ayda" };
  }, [year, today]);

  useEffect(() => {
    document.title = "Analiz — Çalışma Raporu";
  }, []);

  useEffect(() => {
    let alive = true;
    setEntries(null);
    Promise.all([
      api<Entry[]>(`/api/entries?from=${from}&to=${to}`),
      api<Client[]>("/api/clients"),
      api<{ min: string | null }>("/api/entries/bounds"),
    ])
      .then(([list, cl, bounds]) => {
        if (!alive) return;
        setEntries(list);
        setClients(cl);
        if (bounds.min) setMinYear(Math.min(fromISO(bounds.min).getFullYear(), fromISO(today).getFullYear()));
      })
      .catch(e => alive && setError((e as Error).message));
    return () => {
      alive = false;
    };
  }, [from, to, today]);

  const stats = useMemo(() => (entries ? computeStats(entries, from, to, today) : null), [entries, from, to, today]);
  const years = Array.from({ length: fromISO(today).getFullYear() - minYear + 1 }, (_, i) => fromISO(today).getFullYear() - i);

  const showTip = (e: { clientX: number; clientY: number }, text: ReactNode) =>
    setTip({ x: e.clientX, y: e.clientY, text });
  const hideTip = () => setTip(null);

  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-4">
          <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-blue-700 text-sm font-bold text-white">
            R
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold leading-tight">Analiz</h1>
            <p className="truncate text-sm text-slate-500">Ne zaman, ne kadar, kimin için çalıştım?</p>
          </div>
          <Link to="/" className="btn-secondary">
            ← Rapor
          </Link>
          <button onClick={logout} className="btn-ghost text-sm max-sm:hidden" title="Çıkış yap">
            Çıkış
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-4 px-4 py-6">
        {error && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
        )}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Tile label="Toplam kayıt" value={stats?.total} />
          <Tile label="Aktif gün" value={stats?.activeDays} />
          <Tile label="Mevcut seri" value={stats?.currentStreak} unit="gün" />
          <Tile label="En uzun seri" value={stats?.longestStreak} unit="gün" />
        </div>

        <Card>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-semibold">
              {stats ? `${stats.total} kayıt ${label}` : "…"}
            </h2>
            <div className="flex flex-wrap gap-1">
              {[0, ...years].map(y => (
                <button
                  key={y}
                  onClick={() => setSearchParams(y ? { yil: String(y) } : {}, { replace: true })}
                  className={`rounded-md px-3 py-1 text-sm font-medium transition ${
                    y === year ? "bg-blue-700 text-white" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {y || "Son 12 ay"}
                </button>
              ))}
            </div>
          </div>
          {stats && (
            <Heatmap
              from={from}
              to={to}
              today={today}
              counts={stats.byDay}
              onHover={showTip}
              onLeave={hideTip}
              onPick={date => navigate(`/gunluk/${date}`)}
            />
          )}
        </Card>

        {stats && stats.total === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-slate-500">
            Bu dönemde kayıt yok. Bir kayıt eklediğinde grafikler burada oluşur.
          </div>
        ) : (
          stats && (
            <div className="grid gap-4 lg:grid-cols-2">
              <Card title="Haftalık aktivite" subtitle="Son 12 hafta · kayıt sayısı" className="lg:col-span-2">
                <Columns
                  data={stats.weeks.map(w => ({
                    key: w.start,
                    label: fmtShort.format(fromISO(w.start)),
                    value: w.count,
                    tip: `${fmtShort.format(fromISO(w.start))} haftası · ${w.count} kayıt`,
                  }))}
                  onHover={showTip}
                  onLeave={hideTip}
                />
              </Card>

              <Card title="Müşteri dağılımı" subtitle="Seçili dönemdeki kayıtlar">
                <ClientBars entries={entries!} clients={clients} />
              </Card>

              <Card title="Haftanın günleri" subtitle="Hangi gün kaç kayıt">
                <Columns
                  data={stats.weekdays.map((n, i) => ({
                    key: WEEKDAYS[i]!,
                    label: WEEKDAYS[i]!,
                    value: n,
                    tip: `${WEEKDAYS[i]} · ${n} kayıt`,
                  }))}
                  onHover={showTip}
                  onLeave={hideTip}
                  height={160}
                />
              </Card>
            </div>
          )
        )}
      </main>

      {tip && (
        <div
          role="tooltip"
          className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full rounded-md bg-slate-900 px-2.5 py-1.5 text-xs whitespace-nowrap text-white shadow-lg"
          style={{ left: tip.x, top: tip.y - 10 }}
        >
          {tip.text}
        </div>
      )}
    </div>
  );
}

function computeStats(entries: Entry[], from: string, to: string, today: string) {
  const byDay = new Map<string, number>();
  const weekdays = [0, 0, 0, 0, 0, 0, 0];
  for (const e of entries) {
    byDay.set(e.date, (byDay.get(e.date) ?? 0) + 1);
    weekdays[weekdayIndex(e.date)]!++;
  }

  // Seriler: kayıt olan ardışık günler
  const last = to < today ? to : today;
  let longest = 0;
  let run = 0;
  for (let d = fromISO(from); toISO(d) <= last; d = addDays(d, 1)) {
    run = byDay.has(toISO(d)) ? run + 1 : 0;
    longest = Math.max(longest, run);
  }
  // Mevcut seri: bugün henüz kayıt yoksa dünden geriye sayılır
  let current: number | undefined;
  if (to >= today) {
    current = 0;
    let d = fromISO(today);
    if (!byDay.has(today)) d = addDays(d, -1);
    while (toISO(d) >= from && byDay.has(toISO(d))) {
      current++;
      d = addDays(d, -1);
    }
  }

  // Son 12 hafta (dönemin son haftasında biter)
  const lastMonday = mondayOf(fromISO(last));
  const weeks = Array.from({ length: 12 }, (_, i) => {
    const start = addDays(lastMonday, (i - 11) * 7);
    let count = 0;
    for (let k = 0; k < 7; k++) count += byDay.get(toISO(addDays(start, k))) ?? 0;
    return { start: toISO(start), count };
  });

  return {
    byDay,
    total: entries.length,
    activeDays: byDay.size,
    longestStreak: longest,
    currentStreak: current,
    weeks,
    weekdays,
  };
}

function Heatmap({
  from,
  to,
  today,
  counts,
  onHover,
  onLeave,
  onPick,
}: {
  from: string;
  to: string;
  today: string;
  counts: Map<string, number>;
  onHover: (e: { clientX: number; clientY: number }, text: ReactNode) => void;
  onLeave: () => void;
  onPick: (date: string) => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const CELL = 12;
  const GAP = 3;
  const STEP = CELL + GAP;
  const LEFT = 30;
  const TOP = 18;

  const weeks = useMemo(() => {
    const cols: { date: string; inRange: boolean }[][] = [];
    const end = fromISO(to);
    for (let d = mondayOf(fromISO(from)); d <= end; d = addDays(d, 7)) {
      cols.push(
        Array.from({ length: 7 }, (_, k) => {
          const iso = toISO(addDays(d, k));
          return { date: iso, inRange: iso >= from && iso <= to && iso <= today };
        }),
      );
    }
    return cols;
  }, [from, to, today]);

  // Ay adları: ayın ilk gününü içeren haftanın üstüne
  const months = weeks.flatMap((col, i) => {
    const first = col.find(c => c.date.endsWith("-01") && c.date >= from);
    return first ? [{ x: LEFT + i * STEP, text: fmtMonth.format(fromISO(first.date)) }] : [];
  });

  // Mobilde en yeni haftalar görünsün
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [weeks]);

  const width = LEFT + weeks.length * STEP;
  const height = TOP + 7 * STEP;

  return (
    <div>
      <div ref={scroller} className="overflow-x-auto pb-1">
        <svg width={width} height={height} className="block" role="img" aria-label="Günlük kayıt takvimi">
          {months.map(m => (
            <text key={m.x} x={m.x} y={11} className="fill-slate-500 text-[11px]">
              {m.text}
            </text>
          ))}
          {[0, 2, 4].map(r => (
            <text key={r} x={0} y={TOP + r * STEP + CELL - 2} className="fill-slate-500 text-[11px]">
              {WEEKDAYS[r]}
            </text>
          ))}
          {weeks.map((col, i) =>
            col.map((c, r) => {
              if (!c.inRange) return null;
              const n = counts.get(c.date) ?? 0;
              const text = (
                <>
                  <strong>{n ? `${n} kayıt` : "Kayıt yok"}</strong> · {formatDayLong(c.date)}
                </>
              );
              return (
                <rect
                  key={c.date}
                  x={LEFT + i * STEP}
                  y={TOP + r * STEP}
                  width={CELL}
                  height={CELL}
                  rx={2.5}
                  fill={HEAT[level(n)]}
                  stroke={c.date === today ? "#0f172a" : "rgb(15 23 42 / 0.06)"}
                  strokeWidth={c.date === today ? 1.5 : 1}
                  className="cursor-pointer outline-none"
                  tabIndex={0}
                  aria-label={`${formatDayLong(c.date)}: ${n} kayıt`}
                  onMouseMove={e => onHover(e, text)}
                  onMouseLeave={onLeave}
                  onFocus={e => {
                    const r = e.currentTarget.getBoundingClientRect();
                    onHover({ clientX: r.left + r.width / 2, clientY: r.top }, text);
                  }}
                  onBlur={onLeave}
                  onClick={() => onPick(c.date)}
                  onKeyDown={e => e.key === "Enter" && onPick(c.date)}
                />
              );
            }),
          )}
        </svg>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3 text-xs text-slate-500">
        <span>Bir güne tıklayınca o günün raporu açılır.</span>
        <span className="flex items-center gap-1">
          Az
          {HEAT.map((c, i) => (
            <span
              key={c}
              title={LEVEL_TIPS[i]}
              className="size-3 rounded-[3px] border border-slate-900/5"
              style={{ backgroundColor: c }}
            />
          ))}
          Çok
        </span>
      </div>
    </div>
  );
}

// Tek seri sütun grafik: ince sütunlar, üst köşeler yuvarlak, sade ızgara
function Columns({
  data,
  onHover,
  onLeave,
  height = 200,
}: {
  data: { key: string; label: string; value: number; tip: string }[];
  onHover: (e: { clientX: number; clientY: number }, text: ReactNode) => void;
  onLeave: () => void;
  height?: number;
}) {
  const [active, setActive] = useState<string | null>(null);
  const max = Math.max(1, ...data.map(d => d.value));
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1]!;
  const W = 100 / data.length;
  const peak = data.reduce((a, b) => (b.value > a.value ? b : a), data[0]!);

  return (
    <div className="flex flex-col" style={{ height }}>
      <div className="flex min-h-0 flex-1 gap-2">
        {/* y ekseni: çizim alanıyla aynı yükseklikte, böylece tikler ızgarayla hizalı */}
        <div className="relative w-6 shrink-0 text-right text-[11px] text-slate-400">
          {ticks.map(t => (
            <span key={t} className="absolute right-0 -translate-y-1/2" style={{ top: `${(1 - t / top) * 100}%` }}>
              {t}
            </span>
          ))}
        </div>
        <div className="relative min-w-0 flex-1">
          {ticks.map(t => (
            <div
              key={t}
              className={`absolute inset-x-0 border-t ${t === 0 ? "border-slate-300" : "border-slate-100"}`}
              style={{ top: `${(1 - t / top) * 100}%` }}
            />
          ))}
          <div className="absolute inset-0 flex">
            {data.map(d => (
              // Hit alanı: sütunun tamamı (çubuktan büyük)
              <div
                key={d.key}
                className="relative flex h-full items-end justify-center"
                style={{ width: `${W}%` }}
                onMouseMove={e => {
                  setActive(d.key);
                  onHover(e, d.tip);
                }}
                onMouseLeave={() => {
                  setActive(null);
                  onLeave();
                }}
              >
                {active === d.key && <div className="absolute inset-y-0 inset-x-[2px] rounded bg-slate-900/[0.04]" />}
                {d === peak && d.value > 0 && (
                  <span
                    className="absolute text-[11px] font-medium text-slate-700"
                    style={{ bottom: `calc(${(d.value / top) * 100}% + 4px)` }}
                  >
                    {d.value}
                  </span>
                )}
                <div
                  className="relative w-[60%] max-w-7 rounded-t"
                  style={{ height: `${(d.value / top) * 100}%`, backgroundColor: BAR, minHeight: d.value ? 2 : 0 }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex gap-2 pt-1.5">
        <div className="w-6 shrink-0" />
        <div className="flex min-w-0 flex-1">
          {data.map((d, i) => (
            <span
              key={d.key}
              className="truncate text-center text-[11px] text-slate-500"
              // Kalabalıksa ikide bir etiket; en yeni (sağdaki) her zaman görünür
              style={{ width: `${W}%`, visibility: data.length > 8 && (data.length - 1 - i) % 2 ? "hidden" : "visible" }}
            >
              {d.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// 0'dan başlayan 3–5 adet yuvarlak tik değeri
function niceTicks(max: number) {
  const step = max <= 4 ? 1 : max <= 10 ? 2 : max <= 25 ? 5 : Math.ceil(max / 5 / 10) * 10;
  const top = Math.ceil(max / step) * step;
  return Array.from({ length: top / step + 1 }, (_, i) => i * step);
}

function ClientBars({ entries, clients }: { entries: Entry[]; clients: Client[] }) {
  const byId = new Map(clients.map(c => [c.id, c]));
  const groups = groupByClient(entries);
  const max = Math.max(1, ...groups.map(g => g.entries.length));
  return (
    <ul className="space-y-3">
      {groups.map(g => {
        const n = g.entries.length;
        const pct = Math.round((n / entries.length) * 100);
        return (
          <li key={g.id}>
            <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
              <span className="truncate font-medium">{g.name}</span>
              <span className="shrink-0 text-slate-500">
                {n} kayıt · %{pct}
              </span>
            </div>
            <div className="h-2.5 rounded-sm bg-slate-100">
              <div
                className="h-full rounded-sm"
                style={{ width: `${(n / max) * 100}%`, backgroundColor: byId.get(g.id)?.color || DEFAULT_ACCENT }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function Card({
  title,
  subtitle,
  className = "",
  children,
}: {
  title?: string;
  subtitle?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`rounded-xl border border-slate-200 bg-white p-4 shadow-sm ${className}`}>
      {title && (
        <div className="mb-4">
          <h2 className="font-semibold">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
      )}
      {children}
    </section>
  );
}

function Tile({ label, value, unit }: { label: string; value: number | undefined; unit?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-2xl font-semibold text-slate-900">
        {value ?? "—"}
        {unit && value !== undefined && <span className="ml-1 text-sm font-normal text-slate-500">{unit}</span>}
      </div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}
